import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { userAPI } from '../services/api'
import { localizeApiDetail } from '../i18n/apiError'
import { MessageSquare, Bug, Lightbulb, Send, Upload, X, CheckCircle } from 'lucide-react'

type FeedbackType = 'BUG' | 'REQUEST' | 'FEEDBACK'

// Label keys, not labels: translated at render so a language switch applies (#714)
const feedbackTypes = [
  { value: 'BUG', labelKey: 'feedback.types.bug.label', icon: Bug, descriptionKey: 'feedback.types.bug.description' },
  { value: 'REQUEST', labelKey: 'feedback.types.request.label', icon: Lightbulb, descriptionKey: 'feedback.types.request.description' },
  { value: 'FEEDBACK', labelKey: 'feedback.types.feedback.label', icon: MessageSquare, descriptionKey: 'feedback.types.feedback.description' },
] as const

export default function Feedback() {
  const { t } = useTranslation('account')
  const { user } = useAuth()
  const [type, setType] = useState<FeedbackType>('BUG')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [image, setImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImageChange = (file: File | null) => {
    if (!file) {
      setImage(null)
      setImagePreview(null)
      return
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      setError(t('feedback.errors.invalidImageType'))
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setError(t('feedback.errors.imageTooLarge'))
      return
    }

    setImage(file)
    setError('')
    const reader = new FileReader()
    reader.onloadend = () => setImagePreview(reader.result as string)
    reader.readAsDataURL(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleImageChange(file)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const formData = new FormData()
      formData.append('type', type)
      formData.append('subject', subject)
      formData.append('body', body)
      if (image) {
        formData.append('image', image)
      }

      await userAPI.submitFeedback(formData)
      setSuccess(true)
      setSubject('')
      setBody('')
      setImage(null)
      setImagePreview(null)
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || t('feedback.errors.submitFailed'))
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="card p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('feedback.success.title')}</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{t('feedback.success.body')}</p>
          <button
            onClick={() => setSuccess(false)}
            className="btn-primary"
          >
            {t('feedback.success.sendAnother')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <MessageSquare className="w-6 h-6" />
          {t('feedback.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          {t('feedback.intro')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Sender info */}
        <div className="card p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('feedback.sentFrom')} <span className="font-medium text-gray-900 dark:text-white">{user?.email}</span>
            {user?.username && <span className="text-gray-500 dark:text-gray-400"> ({user.username})</span>}
          </p>
        </div>

        {/* Type selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('feedback.type')}</label>
          <div className="grid grid-cols-3 gap-3">
            {feedbackTypes.map((ft) => (
              <button
                key={ft.value}
                type="button"
                onClick={() => setType(ft.value)}
                className={`flex flex-col items-center p-4 rounded-lg border-2 transition ${
                  type === ft.value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400'
                }`}
              >
                <ft.icon className="w-6 h-6 mb-2" />
                <span className="text-sm font-medium">{t(ft.labelKey)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Subject */}
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('feedback.subject')}
          </label>
          <input
            id="subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="input-field w-full"
            placeholder={t('feedback.subjectPlaceholder')}
            required
            maxLength={200}
          />
        </div>

        {/* Body */}
        <div>
          <label htmlFor="body" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('feedback.description')}
          </label>
          <textarea
            id="body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="input-field w-full"
            rows={6}
            placeholder={t('feedback.descriptionPlaceholder')}
            required
            maxLength={5000}
          />
        </div>

        {/* Image upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('feedback.image')}
          </label>
          {imagePreview ? (
            <div className="relative inline-block">
              <img
                src={imagePreview}
                alt={t('feedback.previewAlt')}
                className="max-h-48 rounded-lg border border-gray-200 dark:border-dark-border"
              />
              <button
                type="button"
                onClick={() => handleImageChange(null)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 dark:border-dark-border rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 dark:hover:border-primary-500 transition"
            >
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('feedback.uploadPrompt')}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {t('feedback.uploadHint')}
              </p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            onChange={(e) => handleImageChange(e.target.files?.[0] || null)}
            className="hidden"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={loading || !subject.trim() || !body.trim()}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {loading ? (
            <>{t('feedback.sending')}</>
          ) : (
            <>
              <Send className="w-4 h-4" />
              {t('feedback.submit')}
            </>
          )}
        </button>
      </form>
    </div>
  )
}
